import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ManufacturingPage from "@/pages/ManufacturingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CityPage from "@/pages/CityPage";
import KeywordPage from "@/pages/KeywordPage";
import { KEYWORD_PAGES } from "@/constants/keywordPages";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import CostCalculatorPage from "@/pages/CostCalculatorPage";
import { captureAttribution } from "@/utils/attribution";
import HomePageV2 from "@/pages/HomePageV2";
import CareersPage from "@/pages/CareersPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import CookieBanner from "@/components/CookieBanner";
import { ConsentProvider } from "@/context/ConsentContext";
import { trackPageView } from "@/utils/analytics";
import { CITIES } from "@/constants/business";
import { PRODUCTS } from "@/constants/products";
import { POSTS } from "@/constants/blog";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    captureAttribution();
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ConsentProvider>
        <div className="App bg-[#faf7f2] text-[#1a1a1a] min-h-screen">
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
          </BrowserRouter>
        </div>
      </ConsentProvider>
    </HelmetProvider>
  );
}

function ChromeWrapper({ children }) {
  const { pathname } = useLocation();
  const isV2 = pathname === "/preview-v2";
  return (
    <>
      {!isV2 && <Navigation />}
      <main>{children}</main>
      {!isV2 && <Footer />}
      {!isV2 && <WhatsAppFloat />}
      <CookieBanner />
    </>
  );
}

function AppRoutes() {
  return (
    <ChromeWrapper>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/preview-v2" element={<HomePageV2 />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/products" element={<ProductsPage />} />
        {PRODUCTS.map((p) => (
          <Route key={p.slug} path={`/products/${p.slug}`} element={<ProductDetailPage slug={p.slug} />} />
        ))}
        <Route path="/products/*" element={<Navigate to="/products" replace />} />
        <Route path="/manufacturing" element={<ManufacturingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/elevator-cost-calculator" element={<CostCalculatorPage />} />
        <Route path="/tools/elevator-cost-calculator" element={<CostCalculatorPage />} />
        {POSTS.map((p) => (
          <Route key={p.slug} path={`/blog/${p.slug}`} element={<BlogPostPage slug={p.slug} />} />
        ))}
        <Route path="/blog/*" element={<Navigate to="/blog" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/terms-of-service" element={<TermsPage />} />
        {CITIES.map((c) => (
          <Route key={c.slug} path={`/elevators-${c.slug}`} element={<CityPage slug={c.slug} />} />
        ))}
        {KEYWORD_PAGES.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<KeywordPage slug={p.slug} />} />
        ))}
        <Route path="*" element={<HomePage />} />
      </Routes>
    </ChromeWrapper>
  );
}

function App2Legacy() {
  return (
    <HelmetProvider>
      <ConsentProvider>
        <div className="App bg-[#faf7f2] text-[#1a1a1a] min-h-screen">
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
          </BrowserRouter>
        </div>
      </ConsentProvider>
    </HelmetProvider>
  );
}

export default App;
